// Modo "Asistente de marketing" — sheet del agente V2 inline. Tiene dos
// estados visuales:
//
//   • Empty (sin mensajes): título "Crea sin límites" centrado verticalmente
//     + input + sugerencias debajo (pills). Es la "home" del asistente.
//   • Active (después del primer mensaje): el input se ancla al pie del
//     sheet y el área superior se convierte en el scroll de la conversación
//     (mensajes user/assistant + typing dots + tool cards). Mismo agente y
//     mismas tool cards que el ChatPanelV2 standalone — los componentes de
//     mensaje se importan exportados desde ahí para no duplicar lógica.
//
// El cluster de blurs azules viaja por detrás de TODO el contenido en
// ambos estados — funciona como aura ambiente, no es parte del header
// del empty state.

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plus,
  Upload,
  Package,
  User,
  Image as ImageIcon,
  Play,
  Search,
  Instagram,
  Smartphone,
  Layers,
  ArrowUp,
  Menu,
  History,
  Coins,
  Check,
  Brain,
  Trash2,
  X,
} from 'lucide-react';
import { type UseV2ChatReturn } from '../useV2Chat';
import { listChats, type AgentChatSummary } from '../agentChatApi';
import {
  MessageRow,
  ThinkingIndicator,
  ToolCardRenderer,
  ProductFullMenu,
} from '../ChatPanelV2';
import {
  ModelPickerSheet,
  modelSelectionLabel,
  type ModelSelection,
} from '../ModelPicker';
import { useUserProducts } from '../productsContext';
import { useUserModels } from '../modelsContext';
import { useAgentEnv } from '../agentEnvContext';
import {
  uploadReferenceImage,
  type ChatAttachment,
} from '../agentAttachments';
import GeneratedImagesPicker, {
  type GeneratedPick,
} from '../GeneratedImagesPicker';
import {
  type Intelligence,
  intelligenceToModel,
  INTELLIGENCE_LABEL,
} from '../agentModels';
import {
  fetchMemories,
  deleteMemory,
  type AgentMemory,
} from '../agentMemoryApi';

// Sugerencias mostradas como pills debajo del input. Al tocar una pill,
// el textarea se rellena con `prompt` y queda focuseado al final del
// texto para que el user pueda seguir tipeando sin tap extra. Cada icon
// usa un color de la paleta de los blurs azules de fondo (brand primary
// → azul medio → cyan), así las pills suman al tono del aura sin
// chocar con el texto blanco del título.
const PROMPT_SUGGESTIONS: Array<{
  label: string;
  prompt: string;
  Icon: React.ComponentType<{
    className?: string;
    style?: React.CSSProperties;
  }>;
  iconColor: string;
}> = [
  {
    label: 'Generar una imagen',
    prompt: 'Generá una imagen de ',
    Icon: ImageIcon,
    iconColor: '#1644EB', // brand primary — azul profundo
  },
  {
    label: 'Generar un video',
    prompt: 'Generá un video de ',
    Icon: Play,
    iconColor: '#3B82F6', // azul medio (mismo que el blob de la derecha)
  },
  {
    label: 'Investigar mi competencia',
    prompt: 'Investigá qué está haciendo mi competencia en ',
    Icon: Search,
    iconColor: '#0891B2', // cyan más saturado para que contraste bien sobre el bg blanco del pill (el blob #06B6D4 era demasiado claro como text color)
  },
  {
    label: 'Analizá mi Instagram',
    prompt: 'Analizá mi página de Instagram @',
    Icon: Instagram,
    iconColor: '#E1306C', // rosa de Instagram
  },
];

export interface AsistenteMarketingSheetProps {
  animated: boolean;
  sidebarWidth: number;
  onClose: () => void;
  /** Instancia del chat del agente V2 — viene del padre (InterfazV2) para
   *  que se mantenga vivo aunque el sheet se cierre y reabra (el padre la
   *  resetea cuando corresponde). El sheet la consume vía MessageRow /
   *  ToolCardRenderer / sendMessage. */
  chat: UseV2ChatReturn;
}

export const AsistenteMarketingSheet: React.FC<AsistenteMarketingSheetProps> = ({
  animated,
  sidebarWidth,
  onClose,
  chat,
}) => {
  const [prompt, setPrompt] = useState<string>('');
  // Foco del textarea. En mobile, cuando el input toma foco (aparece el
  // teclado) en el empty-state, ocultamos sutilmente "Crea sin límites" y
  // mostramos las sugerencias sin su pill.
  const [inputFocused, setInputFocused] = useState<boolean>(false);
  // Cartelito "el asistente consume créditos" — el user lo puede descartar con
  // la x; lo persistimos en localStorage para no volver a mostrarlo.
  const [creditsDismissed, setCreditsDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('atlas-v2-credits-notice-dismissed') === '1';
    } catch {
      return false;
    }
  });
  const dismissCreditsNotice = () => {
    setCreditsDismissed(true);
    try {
      localStorage.setItem('atlas-v2-credits-notice-dismissed', '1');
    } catch {
      /* noop */
    }
  };
  // Mobile: con el teclado abierto fijamos el sheet al área visible exacta que
  // reporta visualViewport (top = offsetTop, height = vv.height) en vez de
  // dejarlo `inset-0`. Así el input queda justo arriba del teclado y se compensa
  // el scroll que hace iOS del visual viewport (sin esto el contenido se corría
  // hacia arriba y el input quedaba cortado). Vacío (= inset-0) con teclado
  // cerrado / desktop. Lo recalcula el efecto de visualViewport.
  const [vvStyle, setVvStyle] = useState<React.CSSProperties>({});
  // Nivel de "inteligencia" (modelo). Persistido en localStorage; default
  // 'media' (Sonnet). 'alta' usa Opus. Se sincroniza con chat.setModel.
  const [intelligence, setIntelligence] = useState<Intelligence>(() => {
    try {
      const v = localStorage.getItem('atlas-v2-intelligence');
      if (v === 'baja' || v === 'media' || v === 'alta') return v;
    } catch {
      /* noop */
    }
    return 'media';
  });
  const [intelMenuOpen, setIntelMenuOpen] = useState(false);
  const setModel = chat.setModel; // estable (useCallback) — evita re-runs
  useEffect(() => {
    setModel(intelligenceToModel(intelligence));
    try {
      localStorage.setItem('atlas-v2-intelligence', intelligence);
    } catch {
      /* noop */
    }
  }, [intelligence, setModel]);
  // Menú "+" de adjuntos — abierto/cerrado. Mismo state para mobile
  // (bottom sheet) y desktop (popup), el render distingue por
  // breakpoint.
  const [attachOpen, setAttachOpen] = useState<boolean>(false);
  // Vista interna del menú de adjuntos. `root` es la pantalla principal
  // (Subir imagen + Seleccionar producto / modelo / Otras fuentes).
  // `fuentes` es la sub-vista de "Otras fuentes" (Imagen ya generada,
  // Sacar foto, Cargar desde el móvil — stubs por ahora).
  const [attachView, setAttachView] = useState<'root' | 'fuentes'>('root');
  // Reset de la sub-vista al cerrar — si el user entró a `fuentes` y
  // cerró sin elegir nada, la próxima vez que abra el menú arranca de
  // vuelta en `root`.
  useEffect(() => {
    if (!attachOpen) setAttachView('root');
  }, [attachOpen]);
  // Cerrar el menú "+" al tocar/clickear afuera (desktop popup y, redundante
  // con el overlay, mobile sheet). El marcador `data-attach-menu` está tanto
  // en el ancla desktop como en el sheet mobile, así un click adentro no lo
  // cierra.
  useEffect(() => {
    if (!attachOpen) return;
    const onOutside = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (t && t.closest('[data-attach-menu]')) return;
      setAttachOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('touchstart', onOutside);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('touchstart', onOutside);
    };
  }, [attachOpen]);
  const closeAttach = () => setAttachOpen(false);

  // ── Adjuntos (menú "+") ─────────────────────────────────────────────
  // Picker abierto desde el menú "+": producto o modelo. La imagen usa un
  // <input type=file> oculto, no un picker.
  const [attachPicker, setAttachPicker] = useState<
    'none' | 'product' | 'model' | 'generated'
  >('none');
  const [imageUploading, setImageUploading] = useState(false);
  // Adjunto que se está viendo ampliado (lightbox). null = cerrado.
  const [previewAttachment, setPreviewAttachment] =
    useState<ChatAttachment | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const userProducts = useUserProducts();
  const userModels = useUserModels();
  const { userId, companyId, refreshModels } = useAgentEnv();

  // Subir imagen de referencia: abre el file picker; al elegir, sube al
  // storage y agrega un chip de imagen.
  const onPickImageFile = async (file: File | null) => {
    if (!file || !companyId) return;
    setImageUploading(true);
    try {
      const { storagePath, previewUrl } = await uploadReferenceImage(
        file,
        companyId,
      );
      chat.addAttachment({
        kind: 'image',
        id: `img-${Date.now()}`,
        storagePath,
        previewUrl,
      });
    } catch (e) {
      console.error('[AsistenteMode] subir imagen falló', e);
    } finally {
      setImageUploading(false);
    }
  };

  // Aceptar productos del ProductFullMenu → chips de producto.
  const onAcceptProducts = (ids: string[]) => {
    for (const id of ids) {
      const p = userProducts.find((x) => x.id === id);
      if (!p) continue;
      chat.addAttachment({
        kind: 'product',
        id: `prod-${id}`,
        productId: id,
        name: p.name,
        imageUrl: p.imageUrl,
      });
    }
    setAttachPicker('none');
  };

  // Elegir una o varias imágenes ya generadas → chips de referencia (bucket
  // `generadas`).
  const onAcceptGenerated = (picks: GeneratedPick[]) => {
    picks.forEach((pick, i) => {
      chat.addAttachment({
        kind: 'image',
        id: `gen-${Date.now()}-${i}`,
        storagePath: pick.storagePath,
        previewUrl: pick.previewUrl,
        bucket: pick.bucket,
      });
    });
    setAttachPicker('none');
  };

  // Elegir modelo del ModelPickerSheet → chip de modelo (único).
  const onPickModel = (sel: ModelSelection) => {
    chat.addAttachment({
      kind: 'model',
      id: 'model-attachment',
      selection: sel,
      name: modelSelectionLabel(sel),
      imageUrl:
        sel.mode === 'preset'
          ? sel.imageUrl
          : sel.mode === 'upload'
            ? sel.previewUrl
            : null,
    });
    setAttachPicker('none');
  };

  const promptRef = useRef<HTMLTextAreaElement | null>(null);
  // Ref al scroll de mensajes + al último mensaje del user (lo anclamos
  // arriba al enviar, estilo Director de Estudio).
  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const latestUserMsgRef = useRef<HTMLDivElement | null>(null);
  // Mientras está "pineado", re-anclamos el último mensaje del user cerca
  // del tope en cada frame (así el contenido crece debajo y queda todo
  // visible). Se suelta cuando el user scrollea al fondo manualmente.
  const pinUserToTopRef = useRef<boolean>(false);

  // ── Menú hamburguesa + historial + consumo ──────────────────────────
  const [menuOpen, setMenuOpen] = useState(false);
  // Vista interna del menú hamburguesa: 'root' (3 opciones) o 'consumo'
  // (página de créditos gastados, dentro del mismo popover).
  const [menuView, setMenuView] = useState<'root' | 'consumo'>('root');
  // Memoria del agente — panel slide-in (mismo patrón que el historial).
  const [memoriaOpen, setMemoriaOpen] = useState(false);
  const [memoriasList, setMemoriasList] = useState<AgentMemory[]>([]);
  const [memoriasLoading, setMemoriasLoading] = useState(false);
  const [historialOpen, setHistorialOpen] = useState(false);
  const [historialChats, setHistorialChats] = useState<AgentChatSummary[]>([]);
  const [historialLoading, setHistorialLoading] = useState(false);
  // Consumo del chat (créditos = agente LLM + imágenes), leído de la DB.
  const [consumoCredits, setConsumoCredits] = useState<number | null>(null);
  const [consumoLoading, setConsumoLoading] = useState(false);
  // Reset de la sub-vista al cerrar el menú.
  useEffect(() => {
    if (!menuOpen) setMenuView('root');
  }, [menuOpen]);
  // Al entrar a la página de consumo, leer el total real de la DB.
  useEffect(() => {
    if (menuView !== 'consumo') return;
    let alive = true;
    setConsumoLoading(true);
    chat.fetchConsumo().then((n) => {
      if (alive) {
        setConsumoCredits(n);
        setConsumoLoading(false);
      }
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuView]);
  const handleDeleteMemoria = async (id: string) => {
    setMemoriasList((prev) => prev.filter((m) => m.id !== id));
    await deleteMemory(id);
  };
  // Último chat (más reciente) — para el botón "Continuar chat anterior".
  const [lastChat, setLastChat] = useState<AgentChatSummary | null>(null);

  // Autosize del textarea (mismo patrón que el PromptBar de la home).
  useEffect(() => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, [prompt]);

  // Ancla el último mensaje del user cerca del tope, con aire arriba.
  const scrollLatestUserToTop = (behavior: ScrollBehavior = 'smooth') => {
    const container = messagesScrollRef.current;
    const target = latestUserMsgRef.current;
    if (!container || !target) return;
    const cRect = container.getBoundingClientRect();
    const tRect = target.getBoundingClientRect();
    const offset = tRect.top - cRect.top + container.scrollTop - 16;
    container.scrollTo({ top: Math.max(0, offset), behavior });
  };

  // Auto-scroll. Si hay pin activo (turno en curso), mantenemos el mensaje
  // del user arriba. Sino, seguimos al fondo cuando el user ya estaba cerca.
  useEffect(() => {
    const sc = messagesScrollRef.current;
    if (!sc) return;
    if (pinUserToTopRef.current) {
      scrollLatestUserToTop('smooth');
      return;
    }
    const dist = sc.scrollHeight - sc.scrollTop - sc.clientHeight;
    if (dist < 120) {
      sc.scrollTo({ top: sc.scrollHeight, behavior: 'smooth' });
    }
  }, [chat.messages, chat.activeToolCard, chat.streaming]);

  // Loop que re-ancla el mensaje del user arriba mientras el agente trabaja
  // (texto en streaming, cards que aparecen, etc. empujan contenido y
  // pueden moverlo). Se corta al terminar el turno o al soltar el pin.
  useEffect(() => {
    if (!chat.streaming || !pinUserToTopRef.current) return;
    let raf = 0;
    const tick = () => {
      if (!pinUserToTopRef.current) return;
      const container = messagesScrollRef.current;
      const target = latestUserMsgRef.current;
      if (container && target) {
        const cRect = container.getBoundingClientRect();
        const tRect = target.getBoundingClientRect();
        const offset = tRect.top - cRect.top + container.scrollTop - 16;
        const clamped = Math.max(0, offset);
        if (Math.abs(container.scrollTop - clamped) > 1) {
          container.scrollTop = clamped;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [chat.streaming]);

  // Si el user scrollea hasta el fondo, soltamos el pin (quiere seguir el
  // contenido nuevo, no quedarse anclado arriba).
  const handleMessagesScroll = () => {
    const sc = messagesScrollRef.current;
    if (!sc) return;
    const dist = sc.scrollHeight - sc.scrollTop - sc.clientHeight;
    if (dist < 60) pinUserToTopRef.current = false;
  };

  // Soltar el pin ante CUALQUIER scroll manual (touch/wheel), no solo al llegar
  // al fondo. Sin esto, mientras el agente streamea, el RAF de arriba re-fija el
  // scroll cada frame y el usuario NO puede scrollear (rebota a la posición
  // pineada) — y como nunca puede llegar al fondo, la única vía de soltar el pin
  // (`dist < 60`) es inalcanzable. Apenas el user toma control con un gesto, el
  // pin se suelta y el RAF para (chequea pinUserToTopRef.current al inicio del tick).
  const releasePinOnUserScroll = () => {
    pinUserToTopRef.current = false;
  };

  const hasText = prompt.trim().length > 0;
  const hasMessages = chat.messages.length > 0;
  // Mientras el agente está respondiendo o pidiendo una tool, no aceptamos
  // input nuevo — replica el comportamiento del ChatPanelV2.
  const sendDisabled = chat.streaming || !!chat.activeToolCard;
  // Índice del último mensaje del user — para anclarlo arriba.
  const lastUserIdx = (() => {
    for (let i = chat.messages.length - 1; i >= 0; i--) {
      if (chat.messages[i].role === 'user') return i;
    }
    return -1;
  })();

  const handleGenerate = () => {
    if (sendDisabled) return;
    const trimmed = prompt.trim();
    if (!trimmed) return;
    chat.sendMessage(trimmed);
    setPrompt('');
    // Anclar el nuevo mensaje del user arriba, con aire para la respuesta.
    pinUserToTopRef.current = true;
    requestAnimationFrame(() => scrollLatestUserToTop('smooth'));
  };

  // Traer el chat más reciente para el botón "Continuar chat anterior" —
  // solo cuando el chat actual está vacío (sino ya estás en uno).
  useEffect(() => {
    if (hasMessages) {
      setLastChat(null);
      return;
    }
    let alive = true;
    (async () => {
      const chats = await listChats();
      if (alive) setLastChat(chats[0] ?? null);
    })();
    return () => {
      alive = false;
    };
  }, [hasMessages]);

  // Mobile: con el teclado abierto fijamos el sheet al área visible EXACTA que
  // reporta visualViewport (top = offsetTop + height = vv.height) en vez de
  // inset-0. Así el input (al pie del flex) queda justo arriba del teclado y NO
  // se corre cuando iOS scrollea el visual viewport para mostrarlo.
  //
  // OJO: NO usamos `window.innerHeight` como base para detectar el teclado — en
  // iOS es mayor que `visualViewport.height` aun SIN teclado (home indicator /
  // toolbar). La base es el MAYOR `vv.height` observado (= viewport sin teclado);
  // si vv.height baja respecto de esa base, hay teclado abierto.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let baseline = 0;
    let lastWidth = 0;
    const apply = () => {
      if (window.innerWidth >= 768) {
        setVvStyle({});
        return;
      }
      // Reset de la base al cambiar el ancho (rotación), sino queda stale.
      if (lastWidth !== window.innerWidth) {
        lastWidth = window.innerWidth;
        baseline = vv.height;
      }
      if (vv.height > baseline) baseline = vv.height;
      const kb = Math.max(0, baseline - vv.height);
      // Con el teclado abierto, iOS no solo ENCOGE el visual viewport: también
      // lo SCROLLEA (offsetTop > 0) para meter el input en vista. El sheet es
      // `fixed inset-0` (anclado al layout viewport), así que sin compensar ese
      // offset el contenido se corre hacia arriba — el input queda cortado y
      // aparece una franja vacía entre él y el teclado. Lo fijamos EXACTO al
      // área visible: top = offsetTop, height = vv.height (en Android offsetTop
      // es 0, así que queda top:0 + height = área sobre el teclado). Sin teclado
      // (kb≈0) volvemos a inset-0 (vvStyle vacío).
      setVvStyle(
        kb > 1
          ? {
              top: `${Math.max(0, vv.offsetTop)}px`,
              height: `${vv.height}px`,
              bottom: 'auto',
            }
          : {},
      );
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    window.addEventListener('resize', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      window.removeEventListener('resize', apply);
    };
  }, []);

  const openHistorial = async () => {
    setMenuOpen(false);
    setHistorialOpen(true);
    setHistorialLoading(true);
    const chats = await listChats();
    setHistorialChats(chats);
    setHistorialLoading(false);
  };

  const openMemoria = async () => {
    setMenuOpen(false);
    setMemoriaOpen(true);
    if (!companyId) {
      setMemoriasList([]);
      return;
    }
    setMemoriasLoading(true);
    const ms = await fetchMemories(companyId);
    setMemoriasList(ms);
    setMemoriasLoading(false);
  };

  const handleNewChat = () => {
    setMenuOpen(false);
    chat.reset();
  };

  // Fecha relativa cortita para el historial.
  const relativeDate = (iso: string): string => {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'recién';
    if (min < 60) return `hace ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `hace ${h} h`;
    const days = Math.floor(h / 24);
    if (days < 7) return `hace ${days} d`;
    return d.toLocaleDateString();
  };

  // Contenido del menú "+" — JSX compartido entre el popup desktop y el
  // bottom sheet mobile. Tiene 2 sub-vistas internas (state `attachView`):
  //   • root    — Subir imagen (botón primario), Seleccionar producto /
  //               modelo, Otras fuentes (entra a la sub-vista).
  //   • fuentes — Volver + Imagen ya generada / Sacar foto / Cargar
  //               desde el móvil (los 3 son stubs por ahora).
  const attachMenuContent =
    attachView === 'root' ? (
      <div className="flex flex-col gap-1">
        {/* Subir imagen — CTA primario con bg brand-primary, sin chevron.
            Es la acción más común así que rompe el patrón de fila gris. */}
        <button
          type="button"
          onClick={() => {
            closeAttach();
            fileInputRef.current?.click();
          }}
          className="w-full inline-flex items-center justify-center gap-2 py-3 mb-1 rounded-xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[14.5px] font-semibold shadow-sm transition-colors"
        >
          <Upload className="w-4 h-4" />
          Añadir imagen
        </button>
        {/* Las demás opciones (no-imagen): en mobile van agrupadas en un
            container tipo lista con divisores; en desktop (`md:contents`) el
            wrapper desaparece y las filas fluyen como antes en el popup. */}
        <div className="mt-1 rounded-2xl border border-black/[0.06] divide-y divide-black/[0.05] overflow-hidden md:mt-0 md:contents md:rounded-none md:border-0 md:divide-y-0">
          <AttachMenuItem
            icon={<Package className="w-4 h-4" />}
            label="Añadir producto"
            onClick={() => {
              closeAttach();
              setAttachPicker('product');
            }}
          />
          <AttachMenuItem
            icon={<User className="w-4 h-4" />}
            label="Añadir modelo"
            onClick={() => {
              closeAttach();
              setAttachPicker('model');
            }}
          />
          <AttachMenuItem
            icon={<Layers className="w-4 h-4" />}
            label="Añadir desde otras fuentes"
            onClick={() => setAttachView('fuentes')}
          />
        </div>
      </div>
    ) : (
      <div className="flex flex-col gap-1">
        {/* Header "Volver" — link azul a la izquierda para regresar al
            root del menú sin cerrar el popup/sheet. */}
        <button
          type="button"
          onClick={() => setAttachView('root')}
          className="self-start inline-flex items-center gap-0.5 px-2 py-2 text-[13.5px] text-brand-primary hover:opacity-75 transition-opacity"
        >
          <ChevronLeft className="w-4 h-4" />
          Volver
        </button>
        {/* Mismo tratamiento que el root: en mobile las opciones van en un
            container tipo lista con divisores; en desktop (`md:contents`) el
            wrapper desaparece y fluyen como filas del popup. */}
        <div className="mt-1 rounded-2xl border border-black/[0.06] divide-y divide-black/[0.05] overflow-hidden md:mt-0 md:contents md:rounded-none md:border-0 md:divide-y-0">
          <AttachMenuItem
            icon={<ImageIcon className="w-4 h-4" />}
            label="Añadir imagen ya generada"
            onClick={() => {
              closeAttach();
              setAttachPicker('generated');
            }}
          />
          {/* "Añadir desde el móvil" — solo en escritorio (en mobile ya estás
              en el teléfono, no tiene sentido). */}
          <div className="hidden md:block">
            <AttachMenuItem
              icon={<Smartphone className="w-4 h-4" />}
              label="Añadir desde el móvil"
              onClick={closeAttach}
            />
          </div>
        </div>
      </div>
    );

  // Botón "Continuar con el chat anterior" (aparece en empty state si hay
  // un chat previo en la DB).
  const continueButton = lastChat ? (
    <button
      type="button"
      onClick={() => chat.loadChatById(lastChat.id)}
      className="w-full max-w-[680px] inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-white border border-black/[0.08] hover:border-brand-primary/40 text-[14px] font-medium text-brand-black/85 shadow-sm transition-colors"
    >
      <History className="w-4 h-4 text-brand-primary shrink-0" />
      <span className="truncate">Continuar con el chat anterior</span>
    </button>
  ) : null;

  // Contenido del menú hamburguesa. 'root' = 3 opciones; 'consumo' = página
  // de créditos gastados (dentro del mismo popover, con botón Volver).
  const menuContent =
    menuView === 'consumo' ? (
      <div className="flex flex-col">
        <button
          type="button"
          onClick={() => setMenuView('root')}
          className="self-start inline-flex items-center gap-0.5 px-2 py-2 text-[13.5px] text-brand-primary hover:opacity-75 transition-opacity"
        >
          <ChevronLeft className="w-4 h-4" />
          Volver
        </button>
        <div className="px-3 pb-3 pt-1">
          <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
            Consumo de este chat
          </p>
          <div className="flex items-center gap-2 mt-2">
            <img
              src="/images/token_icon.png"
              alt=""
              aria-hidden="true"
              className="w-6 h-6 object-contain"
            />
            <span className="text-[26px] font-bold text-brand-primary leading-none">
              {consumoLoading ? '…' : (consumoCredits ?? 0)}
            </span>
            <span className="text-[13px] font-medium text-neutral-500">
              {(consumoCredits ?? 0) === 1 ? 'crédito' : 'créditos'}
            </span>
          </div>
          <p className="text-[12.5px] text-[#3C3C43]/55 mt-1.5 leading-snug">
            Incluye el costo del asistente y las imágenes generadas en esta
            conversación.
          </p>
        </div>
      </div>
    ) : (
      <div className="flex flex-col gap-1">
        <AttachMenuItem
          icon={<Plus className="w-4 h-4" />}
          label="Nuevo chat"
          onClick={handleNewChat}
        />
        <AttachMenuItem
          icon={<Coins className="w-4 h-4" />}
          label="Consumo"
          onClick={() => setMenuView('consumo')}
        />
        <AttachMenuItem
          icon={<Brain className="w-4 h-4" />}
          label="Memoria"
          onClick={openMemoria}
        />
        <AttachMenuItem
          icon={<History className="w-4 h-4" />}
          label="Historial"
          onClick={openHistorial}
        />
      </div>
    );

  // Lista del historial (loading / vacío / items).
  const historialListContent = historialLoading ? (
    <p className="text-[13.5px] text-[#3C3C43]/55 text-center py-8">Cargando…</p>
  ) : historialChats.length === 0 ? (
    <p className="text-[13.5px] text-[#3C3C43]/55 text-center py-8">
      Todavía no hay chats.
    </p>
  ) : (
    <div className="flex flex-col gap-0.5">
      {historialChats.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => {
            setHistorialOpen(false);
            chat.loadChatById(c.id);
          }}
          className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-neutral-50 active:bg-neutral-100 transition-colors"
        >
          <p className="text-[14.5px] font-medium text-brand-black/90 truncate">
            {c.title || 'Chat'}
          </p>
          <p className="text-[12.5px] text-[#3C3C43]/55 mt-0.5">
            {relativeDate(c.updated_at)}
          </p>
        </button>
      ))}
    </div>
  );

  // Lista de memorias (loading / vacío / items con borrar).
  const memoriaListContent = memoriasLoading ? (
    <p className="text-[13.5px] text-[#3C3C43]/55 text-center py-8">Cargando…</p>
  ) : memoriasList.length === 0 ? (
    <p className="text-[13.5px] text-[#3C3C43]/55 text-center py-8 px-4 leading-snug">
      Todavía no hay nada guardado. El asistente va a recordar tus preferencias
      y datos de tu marca a medida que charlen.
    </p>
  ) : (
    <div className="flex flex-col gap-1">
      {memoriasList.map((m) => (
        <div
          key={m.id}
          className="group flex items-start gap-2 px-3 py-2.5 rounded-xl hover:bg-neutral-50 transition-colors"
        >
          <span className="flex-1 min-w-0 text-[14px] text-brand-black/90 leading-snug">
            {m.content}
          </span>
          <button
            type="button"
            onClick={() => handleDeleteMemoria(m.id)}
            aria-label="Borrar memoria"
            className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#3C3C43]/40 hover:text-red-500 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );

  // Portal a `document.body` para escapar de cualquier stacking context
  // creado por ancestros de InterfazV2 (transform/filter/etc.). Sin esto
  // el `z-[201]` queda atrapado en el árbol y el chrome del Sidebar
  // mobile (z-50) termina pintando por encima del sheet.
  return ReactDOM.createPortal(
    <>
      {/* Backdrop — en mobile invisible (el sheet ocupa toda la pantalla);
          en desktop arranca después del sidebar para no oscurecerlo. El
          sidebar queda completamente clickeable y con su look normal.
          z-30 queda debajo del chrome mobile (z-50) para que pills back /
          créditos / menú sigan visibles arriba. */}
      <div
        className={`fixed inset-0 md:left-[var(--sidebar-w,0px)] z-30 bg-black/0 md:bg-black/30 md:rounded-tl-[26px] md:rounded-bl-[26px] transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        style={
          { '--sidebar-w': `${sidebarWidth}px` } as React.CSSProperties
        }
        onClick={onClose}
      />
      {/* Sheet container.
          • Mobile  → fixed inset-0, slide-up desde abajo. z-40 mantiene
            el sheet POR DEBAJO del chrome (z-50) del Sidebar para que el
            user siga viendo los pills de tokens / menú arriba.
          • Desktop → fixed con left=sidebarWidth, slide desde la derecha
            (mismo patrón que PresetDetailSheet). */}
      <div
        className={`fixed inset-0 z-40 bg-surface-mobile md:bg-surface-content flex flex-col transition-[transform,opacity] duration-300 ease-out md:left-[var(--sidebar-w,0px)] md:rounded-tl-[26px] md:rounded-bl-[26px] md:overflow-hidden ${
          animated
            ? 'translate-y-0 opacity-100'
            : // Mobile → slide-up desde abajo (sheet). Desktop → entrada
              // sutil estilo `mode-swap`: fade + 4px de translateY (mismo
              // lenguaje que el resto de los modos), sin slide horizontal.
              'translate-y-full opacity-100 md:translate-y-1 md:opacity-0'
        }`}
        style={
          // CSS var con el ancho del sidebar — sólo aplica en md+ vía la
          // clase `md:left-[var(--sidebar-w)]` de arriba. En mobile vvStyle
          // suma un padding-bottom = alto del teclado (el sheet sigue
          // inset-0); en md+ vvStyle está vacío.
          {
            '--sidebar-w': `${sidebarWidth}px`,
            ...vvStyle,
          } as React.CSSProperties
        }
      >
        {/* Header — solo visible en desktop con "Volver" texto (mismo
            patrón que el PresetDetailSheet). En mobile el chrome del
            Sidebar ya tiene su back chevron pill (vía mobileBackOverride
            seteado por el useEffect del padre), así que no duplicamos el
            botón acá. */}
        <header className="shrink-0 hidden md:flex px-5 md:px-8 pt-5 md:pt-6 pb-3 items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver
          </button>
        </header>

        {/* Hamburguesa top-right (ambos breakpoints). Abre el menú de 3
            opciones (Nuevo chat / Consumo / Historial). En mobile se ubica
            debajo del chrome del Sidebar; en desktop a la altura del header. */}
        <div className="absolute right-4 md:right-8 z-30 top-[calc(env(safe-area-inset-top,0px)+3.75rem)] md:top-5">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Opciones del chat"
            className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-white/90 backdrop-blur-md border border-black/[0.06] shadow-sm text-brand-black/70 hover:bg-white transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          {/* Popover desde el botón (mismo en mobile y desktop). */}
          {menuOpen && (
            <div className="absolute top-full right-0 mt-2 w-[250px] bg-white border border-black/[0.06] rounded-2xl shadow-xl p-2 z-30">
              {menuContent}
            </div>
          )}
        </div>
        {/* Backdrop para cerrar el menú al tocar afuera (ambos breakpoints). */}
        {menuOpen && (
          <div
            className="fixed inset-0 z-20"
            onClick={() => setMenuOpen(false)}
          />
        )}

        {/* Contenido principal. Layout flex-col que se reparte entre
            zona scrolleable (título/empty O mensajes/active) arriba y
            zona fija (input + sugerencias opcionales) abajo. En mobile
            sumamos `safe-area-inset-top + 3.5rem` (alto del chrome del
            Sidebar) para que el primer elemento no quede debajo del
            back pill / créditos / menú. */}
        <div className="relative flex-1 min-h-0 flex flex-col pt-[calc(env(safe-area-inset-top,0px)+3.5rem)] md:pt-0">
          {/* Cluster de blobs azules de fondo — vive a nivel del sheet,
              detrás de TODO el contenido (z-0). Permanece igual en
              empty y active states: es aura ambiente, no parte del
              header de empty.

              Las CSS variables `--drift-N-dur` controlan el tiempo de
              cada animación drift, overrideando el default de
              `animate-drift-N` (4-5s). En mobile mantenemos el ritmo
              original; en desktop bajamos a ~14-16s para un drift muy
              lento, más calmo y menos competitivo con el texto/input. */}
          <div
            aria-hidden="true"
            // 3 estados de movimiento + opacidad:
            //   • Empty                         → centrado, opacidad full
            //   • Active idle / esperando user  → bajado al pie (sale de
            //     pantalla), opacidad bien baja para no competir con la
            //     tool card o el input cuando el user tiene que actuar.
            //   • Active streaming (agente pensando / respondiendo) →
            //     misma posición que idle pero opacidad sube sutilmente,
            //     como un "respiro" del aura mientras Atlas trabaja.
            //
            // El transform se desliza rápido (250ms) para acompañar el
            // "snap" del input que cambia de rama JSX (empty → active) y
            // no se siente desfasado del container. La opacidad tiene
            // su propio timing más lento (~900ms) — el "respiro" del
            // streaming debe ser sutil, no abrupto.
            className={`pointer-events-none absolute inset-0 z-0 flex items-center justify-center overflow-visible [--drift-1-dur:4s] [--drift-2-dur:5s] [--drift-3-dur:4.5s] md:[--drift-1-dur:14s] md:[--drift-2-dur:16s] md:[--drift-3-dur:15s] ${
              hasMessages
                ? `translate-y-[65%] md:translate-y-[60%] ${
                    chat.streaming ? 'opacity-60' : 'opacity-25'
                  }`
                : inputFocused
                  ? // Mobile: al enfocar el input (teclado), el aura se baja
                    // sutilmente al estado "active idle" anticipando el chat.
                    // Desktop: se mantiene centrado y full (no hay teclado).
                    'translate-y-[65%] opacity-25 md:translate-y-0 md:opacity-100'
                  : 'translate-y-0 opacity-100'
            }`}
            style={{
              transition:
                'transform 250ms ease-out, opacity 900ms ease-out',
            }}
          >
            {/* Entrada: el blur es LO ÚLTIMO en aparecer. Mientras el sheet
                hace su fade/slide (~300ms), el aura se mantiene en 0 y recién
                ahí arranca su propio fade-in (delay-300 + 700ms) para que se
                sienta que "se enciende" después de que el modo ya entró. */}
            <div
              className={`relative w-[420px] h-[420px] md:w-[920px] md:h-[700px] transition-opacity duration-700 ${
                animated ? 'opacity-100 delay-300' : 'opacity-0'
              }`}
            >
              <div
                className="absolute rounded-full blur-3xl animate-drift-1 opacity-85 md:opacity-50"
                style={{
                  width: '60%',
                  height: '55%',
                  backgroundColor: '#1644EB',
                  left: '5%',
                  top: '5%',
                  animationDuration: 'var(--drift-1-dur)',
                }}
              />
              <div
                className="absolute rounded-full blur-3xl animate-drift-2 opacity-80 md:opacity-45"
                style={{
                  width: '55%',
                  height: '55%',
                  backgroundColor: '#3B82F6',
                  right: '5%',
                  top: '15%',
                  animationDuration: 'var(--drift-2-dur)',
                }}
              />
              <div
                className="absolute rounded-full blur-3xl animate-drift-3 opacity-70 md:opacity-40"
                style={{
                  width: '50%',
                  height: '50%',
                  backgroundColor: '#06B6D4',
                  left: '25%',
                  bottom: '5%',
                  animationDuration: 'var(--drift-3-dur)',
                }}
              />
            </div>
          </div>

          {/* Helper JSX: input container compartido entre empty y active.
              No es un sub-componente — vive acá adentro para tener acceso
              a `prompt`, `attachOpen`, refs y handlers sin prop drilling. */}
          {(() => {
            // Fila de chips de adjuntos (imagen / producto / modelo) que el
            // user enganchó desde el menú "+". Se muestra arriba del textarea
            // y se manda como contexto con el próximo mensaje.
            const hasChips = chat.attachments.length > 0 || imageUploading;
            const chipsRow = hasChips ? (
              <div className="flex flex-wrap items-center gap-2 px-3 pt-3">
                {chat.attachments.map((a) => {
                  const thumb =
                    a.kind === 'image'
                      ? a.previewUrl
                      : a.imageUrl;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setPreviewAttachment(a)}
                      className="group relative w-16 h-16 rounded-xl overflow-hidden bg-neutral-100 border border-black/[0.06] cursor-pointer"
                    >
                      {thumb ? (
                        <img
                          src={thumb}
                          alt={a.kind === 'image' ? 'Imagen' : a.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="w-full h-full inline-flex items-center justify-center text-brand-primary">
                          {a.kind === 'model' ? (
                            <User className="w-6 h-6" />
                          ) : (
                            <ImageIcon className="w-6 h-6" />
                          )}
                        </span>
                      )}
                      {/* Botón cerrar dentro de la imagen, esquina superior
                          derecha. Mobile: siempre visible. Desktop: al hover. */}
                      <span
                        role="button"
                        tabIndex={-1}
                        onClick={(e) => {
                          e.stopPropagation();
                          chat.removeAttachment(a.id);
                        }}
                        aria-label="Quitar adjunto"
                        className="absolute top-1 right-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-black/55 hover:bg-black/75 text-white shadow-sm transition-opacity opacity-100 md:opacity-0 md:group-hover:opacity-100"
                      >
                        <X className="w-3 h-3" strokeWidth={2.5} />
                      </span>
                    </button>
                  );
                })}
                {imageUploading && (
                  <div className="w-16 h-16 rounded-xl bg-neutral-100 border border-black/[0.06] inline-flex items-center justify-center">
                    <span className="w-4 h-4 rounded-full border-2 border-brand-primary/30 border-t-brand-primary animate-spin" />
                  </div>
                )}
              </div>
            ) : null;

            // Selector de "inteligencia" (modelo). A la izquierda del botón
            // de enviar. Las 3 opciones explican que las más altas gastan más.
            const intelOptions: {
              id: Intelligence;
              label: string;
              desc: string;
            }[] = [
              { id: 'baja', label: 'Baja', desc: 'Rápida y económica' },
              { id: 'media', label: 'Media', desc: 'Equilibrada · recomendada' },
              {
                id: 'alta',
                label: 'Alta',
                desc: 'Máxima inteligencia · gasta más créditos',
              },
            ];
            const intelligenceControl = (
              <div className="relative shrink-0">
                <button
                  type="button"
                  onClick={() => setIntelMenuOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full transition-colors bg-neutral-100 hover:bg-neutral-200 md:bg-transparent md:hover:bg-neutral-100"
                >
                  <span className="text-[14px] text-brand-black">
                    Inteligencia
                  </span>
                  <span className="text-[14px] font-medium text-[#3C3C43]/55">
                    {INTELLIGENCE_LABEL[intelligence]}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-[#3C3C43]/45 transition-transform ${
                      intelMenuOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {intelMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-[55]"
                      onClick={() => setIntelMenuOpen(false)}
                    />
                    <div className="absolute bottom-full left-0 md:left-auto md:right-0 mb-2 w-[260px] bg-white border border-black/[0.06] rounded-2xl shadow-xl p-1.5 z-[56]">
                      {intelOptions.map((opt) => {
                        const active = intelligence === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => {
                              setIntelligence(opt.id);
                              setIntelMenuOpen(false);
                            }}
                            className={`w-full text-left rounded-xl px-3 py-2.5 transition-colors ${
                              active
                                ? 'bg-brand-primary/[0.06]'
                                : 'hover:bg-neutral-50'
                            }`}
                          >
                            <span className="flex items-center justify-between gap-2">
                              <span className="text-[14px] font-medium text-brand-black/90">
                                {opt.label}
                              </span>
                              {active && (
                                <Check className="w-4 h-4 text-brand-primary shrink-0" />
                              )}
                            </span>
                            <span className="block text-[12px] text-[#3C3C43]/55 mt-0.5 leading-snug">
                              {opt.desc}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );

            const inputBox = (
              <div className="w-full max-w-[680px] bg-white border border-black/[0.06] rounded-2xl shadow-sm ring-1 ring-brand-primary/15">
                {chipsRow}
                <div className="px-5 pt-4">
                  <textarea
                    ref={promptRef}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onFocus={() => setInputFocused(true)}
                    onBlur={() => setInputFocused(false)}
                    onKeyDown={(e) => {
                      // Enter (sin Shift) envía. Shift+Enter = salto de línea.
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleGenerate();
                      }
                    }}
                    placeholder={
                      hasMessages
                        ? 'Pedile algo a Atlas…'
                        : 'Describí lo que querés generar…'
                    }
                    rows={1}
                    disabled={chat.streaming}
                    className="w-full resize-none bg-transparent border-none outline-none text-[15.5px] text-brand-black/90 placeholder:text-[#3C3C43]/35 leading-snug overflow-hidden disabled:cursor-not-allowed"
                  />
                </div>
                {/* Row inferior. Mobile: [+] [Inteligencia pill] … [↑]
                    (inteligencia pegada al + a la izquierda). Desktop:
                    [+] … [Inteligencia] [↑] (a la derecha, como antes). El
                    posicionamiento por breakpoint se logra con los `ml-auto`. */}
                <div className="flex items-center gap-2 px-3 pb-3 pt-2">
                  <div className="relative shrink-0" data-attach-menu>
                    <button
                      type="button"
                      onClick={() => setAttachOpen((v) => !v)}
                      aria-label="Adjuntar"
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 text-brand-black/70 transition-colors"
                    >
                      <Plus className="w-4 h-4" strokeWidth={2.25} />
                    </button>
                    {/* Popup desktop — anclado al botón. Solo md+. El
                        contenido (root / fuentes) lo arma `attachMenuContent`
                        afuera del closure así se comparte con el bottom
                        sheet mobile. */}
                    {attachOpen && (
                      <div className="hidden md:block absolute bottom-full left-0 mb-2 w-[280px] bg-white border border-black/[0.06] rounded-2xl shadow-xl p-2 z-10">
                        {attachMenuContent}
                      </div>
                    )}
                  </div>
                  {/* Inteligencia: mobile pegada al + (left); desktop a la
                      derecha (md:ml-auto la empuja). */}
                  <div className="md:ml-auto">{intelligenceControl}</div>
                  <button
                    type="button"
                    onClick={
                      hasText && !sendDisabled ? handleGenerate : undefined
                    }
                    disabled={sendDisabled}
                    aria-label="Enviar al asistente"
                    className={`relative shrink-0 ml-auto md:ml-0 inline-flex items-center justify-center w-9 h-9 rounded-full shadow-sm transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${
                      hasText
                        ? 'bg-brand-primary hover:bg-brand-primary-hover text-white'
                        : 'bg-neutral-100 hover:bg-neutral-200 text-brand-black/70'
                    }`}
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );

            const suggestions = (
              <div className="w-full flex flex-col items-start gap-2 pl-3 md:pl-0 md:flex-row md:flex-wrap md:items-center md:justify-center">
                {PROMPT_SUGGESTIONS.map(
                  ({ label, prompt: starter, Icon, iconColor }) => (
                    <button
                      key={label}
                      type="button"
                      // Evitar que el tap robe el foco del textarea (mantiene
                      // el teclado abierto y evita el flicker del título).
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setPrompt(starter);
                        requestAnimationFrame(() => {
                          const el = promptRef.current;
                          if (!el) return;
                          el.focus();
                          el.setSelectionRange(
                            starter.length,
                            starter.length,
                          );
                        });
                      }}
                      className={`inline-flex items-center gap-1.5 h-9 rounded-full text-[13.5px] text-brand-black/85 transition-all duration-300 md:px-3.5 md:bg-white md:border md:border-black/[0.08] md:hover:border-brand-primary/30 ${
                        inputFocused
                          ? 'px-0 bg-transparent border-0'
                          : 'px-3.5 bg-white border border-black/[0.08]'
                      }`}
                    >
                      <Icon
                        className="w-3.5 h-3.5"
                        style={{ color: iconColor }}
                      />
                      {label}
                    </button>
                  ),
                )}
              </div>
            );

            const toolCardSlot = chat.activeToolCard && (
              <div
                key={chat.activeToolCard.id}
                className="w-full max-w-[680px] animate-mode-swap overflow-y-auto rounded-2xl bg-white shadow-[0_2px_14px_-6px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.04]"
                style={{ maxHeight: '65dvh' }}
              >
                <ToolCardRenderer
                  card={chat.activeToolCard}
                  chat={chat}
                  embedded
                />
              </div>
            );

            if (!hasMessages) {
              // ── Empty state ──────────────────────────────────────────
              // Desktop: bloque centrado (título + input + atajos) + el
              // "continuar chat" va al footer de abajo.
              // Mobile: el título se centra arriba y el cluster queda
              // anclado al pie en orden atajos → continuar → input (input
              // abajo del todo). Logrado con `order-*` por breakpoint sin
              // duplicar el input (su ref es única).
              return (
                <div
                  className="relative z-10 flex-1 min-h-0 overflow-y-auto flex flex-col px-5 md:px-8 md:items-center md:justify-center md:py-6"
                  style={{
                    paddingBottom:
                      'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)',
                  }}
                >
                  {/* Título — mobile: ocupa el espacio de arriba y se centra;
                      desktop: parte del bloque centrado. Al enfocar el input
                      en mobile (teclado abierto) se desvanece sutilmente
                      (en desktop siempre visible). */}
                  <div
                    className={`order-1 flex-1 md:flex-none flex items-center justify-center md:mb-10 transition-opacity duration-300 md:opacity-100 ${
                      inputFocused ? 'opacity-0' : 'opacity-100'
                    }`}
                  >
                    <h1 className="text-[32px] md:text-[44px] font-normal text-white font-display italic text-center leading-tight drop-shadow-md">
                      Crea sin límites
                    </h1>
                  </div>
                  {/* Input — mobile abajo del todo (order-4); desktop justo
                      bajo el título (order-2). */}
                  <div className="order-4 md:order-2 w-full max-w-3xl mx-auto flex flex-col items-center mt-5 md:mt-0">
                    {/* Aviso de consumo — extensión gris pegada ARRIBA del input
                        (mobile y desktop), con una x para dejar de mostrarlo
                        (persistido en localStorage). */}
                    {!creditsDismissed && (
                      <div className="w-full max-w-[680px] flex items-center gap-1.5 rounded-t-2xl bg-[#F2F2F7] pl-4 pr-1.5 pt-2 pb-4 -mb-3">
                        <p className="flex-1 text-[11.5px] text-brand-black/55 leading-snug">
                          El asistente consumirá créditos basados en su uso.
                        </p>
                        <button
                          type="button"
                          onClick={dismissCreditsNotice}
                          aria-label="No mostrar de nuevo"
                          className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-brand-black/40 hover:text-brand-black/70 hover:bg-black/[0.06] transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    <div className="relative z-10 w-full flex justify-center">
                      {inputBox}
                    </div>
                  </div>
                  {/* Atajos — mobile: ocultos hasta que se enfoca el input
                      (teclado abierto), apilados sobre el input (order-2);
                      desktop: siempre visibles, debajo del input (order-3). */}
                  <div
                    className={`order-2 md:order-3 w-full max-w-3xl mx-auto justify-center mt-4 md:mt-10 transition-opacity duration-300 md:flex md:opacity-100 md:pointer-events-auto ${
                      inputFocused ? 'flex' : 'hidden'
                    } ${
                      inputFocused && hasText
                        ? 'opacity-0 pointer-events-none'
                        : 'opacity-100'
                    }`}
                  >
                    {suggestions}
                  </div>
                </div>
              );
            }

            // ── Active state ───────────────────────────────────────────
            // Mensajes scrolleables arriba, input (o tool card) anclado
            // al pie con safe-area-inset.
            return (
              <>
                <div
                  ref={messagesScrollRef}
                  onScroll={handleMessagesScroll}
                  onWheel={releasePinOnUserScroll}
                  onTouchMove={releasePinOnUserScroll}
                  className="relative z-10 flex-1 min-h-0 overflow-y-auto px-5 md:px-8"
                  style={{ scrollbarWidth: 'thin' }}
                >
                  <div className="max-w-3xl mx-auto flex flex-col gap-4 pt-6 pb-28">
                    {chat.messages.map((msg, i) => (
                      <MessageRow
                        key={i}
                        msg={msg}
                        chat={chat}
                        userRef={i === lastUserIdx ? latestUserMsgRef : undefined}
                      />
                    ))}
                    {(() => {
                      // Indicador de actividad. Si hay una tool server-side en
                      // uso (web search / scraper), mostramos su label real.
                      // Sino, "Pensando…" mientras el agente prepara la
                      // respuesta/card y no hay nada visible todavía.
                      if (chat.streaming && chat.toolActivity) {
                        return <ThinkingIndicator label={chat.toolActivity} />;
                      }
                      const last = chat.messages[chat.messages.length - 1];
                      const preparing =
                        chat.streaming &&
                        !chat.activeToolCard &&
                        (!last ||
                          last.role === 'user' ||
                          (last.role === 'assistant' &&
                            !last.text &&
                            (!last.toolCards || last.toolCards.length === 0)));
                      return preparing ? <ThinkingIndicator /> : null;
                    })()}
                  </div>
                </div>
                <div
                  className="relative z-10 shrink-0 px-5 md:px-8 pt-3"
                  style={{
                    paddingBottom:
                      'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)',
                  }}
                >
                  <div className="max-w-3xl mx-auto flex flex-col items-center">
                    {toolCardSlot ?? inputBox}
                  </div>
                </div>
              </>
            );
          })()}

          {/* Desktop: continuar chat anterior, abajo del todo (solo empty /
              nuevo chat). El aviso de consumo ahora vive arriba del input
              (extensión gris) en ambos breakpoints. */}
          {!hasMessages && continueButton && (
            <div className="hidden md:block relative z-10 shrink-0 px-8 pb-6 pt-2">
              <div className="max-w-3xl mx-auto flex flex-col items-center gap-2">
                {continueButton}
              </div>
            </div>
          )}
        </div>

        {/* ── Historial de chats (slide-in) ── */}
        {historialOpen && (
          <>
            <div
              className="fixed inset-0 z-[63] bg-black/40 animate-fade-in"
              onClick={() => setHistorialOpen(false)}
            />
            {/* Mobile: bottom sheet */}
            <div
              className="md:hidden fixed inset-x-0 bottom-0 z-[64] bg-white rounded-t-[20px] shadow-2xl flex flex-col animate-slide-up-sheet"
              style={{
                maxHeight: '80dvh',
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              }}
            >
              <div className="py-2 shrink-0">
                <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
              </div>
              <div className="flex items-center justify-between px-4 pb-2 shrink-0">
                <h3 className="text-[15px] font-semibold text-brand-black/90">
                  Historial de chats
                </h3>
                <button
                  type="button"
                  onClick={() => setHistorialOpen(false)}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-3 pb-4">
                {historialListContent}
              </div>
            </div>
            {/* Desktop: side panel */}
            <div className="hidden md:flex fixed top-4 right-4 bottom-4 w-[420px] z-[64] bg-white rounded-[13px] shadow-2xl flex-col overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 shrink-0">
                <h3 className="text-[17px] font-semibold text-brand-black/90">
                  Historial de chats
                </h3>
                <button
                  type="button"
                  onClick={() => setHistorialOpen(false)}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="mx-5 h-px bg-black/[0.08] shrink-0" />
              <div className="flex-1 overflow-y-auto px-3 py-3">
                {historialListContent}
              </div>
            </div>
          </>
        )}

        {/* ── Memoria del asistente (slide-in, mismo patrón que historial) ── */}
        {memoriaOpen && (
          <>
            <div
              className="fixed inset-0 z-[63] bg-black/40 animate-fade-in"
              onClick={() => setMemoriaOpen(false)}
            />
            {/* Mobile: bottom sheet */}
            <div
              className="md:hidden fixed inset-x-0 bottom-0 z-[64] bg-white rounded-t-[20px] shadow-2xl flex flex-col animate-slide-up-sheet"
              style={{
                maxHeight: '80dvh',
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              }}
            >
              <div className="py-2 shrink-0">
                <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
              </div>
              <div className="px-4 pb-1 shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-[15px] font-semibold text-brand-black/90">
                    Memoria del asistente
                  </h3>
                  <button
                    type="button"
                    onClick={() => setMemoriaOpen(false)}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                    aria-label="Cerrar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[12.5px] text-[#3C3C43]/55 leading-snug pb-1">
                  Lo que el asistente recuerda de vos y tu marca entre chats.
                </p>
              </div>
              <div className="flex-1 overflow-y-auto px-3 pb-4">
                {memoriaListContent}
              </div>
            </div>
            {/* Desktop: side panel */}
            <div className="hidden md:flex fixed top-4 right-4 bottom-4 w-[420px] z-[64] bg-white rounded-[13px] shadow-2xl flex-col overflow-hidden">
              <div className="px-5 pt-4 pb-3 shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-[17px] font-semibold text-brand-black/90">
                    Memoria del asistente
                  </h3>
                  <button
                    type="button"
                    onClick={() => setMemoriaOpen(false)}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                    aria-label="Cerrar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[13px] text-[#3C3C43]/55 leading-snug mt-1">
                  Lo que el asistente recuerda de vos y tu marca entre chats.
                </p>
              </div>
              <div className="mx-5 h-px bg-black/[0.08] shrink-0" />
              <div className="flex-1 overflow-y-auto px-3 py-3">
                {memoriaListContent}
              </div>
            </div>
          </>
        )}


        {/* Mobile bottom sheet del menú "+". Solo se monta cuando
            attachOpen=true y estamos en mobile. */}
        {attachOpen && (
          <div className="md:hidden fixed inset-0 z-[62]">
            <div
              className="absolute inset-0 bg-black/40 animate-fade-in"
              onClick={() => setAttachOpen(false)}
            />
            <div
              data-attach-menu
              className="absolute inset-x-0 bottom-0 bg-white rounded-t-[20px] shadow-2xl p-3 animate-slide-up-sheet"
              style={{
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              }}
            >
              <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto mb-3" />
              {attachMenuContent}
            </div>
          </div>
        )}
      </div>

      {/* Input de archivo oculto — "Añadir imagen" del menú "+". */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          onPickImageFile(f);
          // Permitir re-elegir el mismo archivo.
          e.target.value = '';
        }}
      />

      {/* Picker real de productos (mismo que el agente / modo modelo). */}
      {attachPicker === 'product' && (
        <ProductFullMenu
          initialSelected={[]}
          allowMulti
          onCancel={() => setAttachPicker('none')}
          onAccept={onAcceptProducts}
        />
      )}

      {/* Picker de imágenes ya generadas. */}
      <GeneratedImagesPicker
        open={attachPicker === 'generated'}
        companyId={companyId}
        onCancel={() => setAttachPicker('none')}
        onAccept={onAcceptGenerated}
      />

      {/* Picker real de modelos. */}
      <ModelPickerSheet
        open={attachPicker === 'model'}
        selection={null}
        onChange={onPickModel}
        onClose={() => setAttachPicker('none')}
        userModels={userModels}
        userId={userId}
        companyId={companyId}
        onModelCreated={refreshModels}
        hideAuto
      />

      {/* Lightbox de adjunto — imagen ampliada con fondo desenfocado. Si es
          producto o modelo, el nombre aparece sutil debajo. */}
      {previewAttachment && (
        <div
          className="fixed inset-0 z-[300] flex flex-col items-center justify-center px-6 bg-black/55 backdrop-blur-md animate-fade-in"
          onClick={() => setPreviewAttachment(null)}
        >
          <button
            type="button"
            onClick={() => setPreviewAttachment(null)}
            aria-label="Cerrar"
            className="absolute top-5 right-5 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white backdrop-blur-sm transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div
            className="flex flex-col items-center gap-4 max-w-[88vw]"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const src =
                previewAttachment.kind === 'image'
                  ? previewAttachment.previewUrl
                  : previewAttachment.imageUrl;
              return src ? (
                <img
                  src={src}
                  alt={
                    previewAttachment.kind === 'image'
                      ? 'Imagen'
                      : previewAttachment.name
                  }
                  className="max-w-full max-h-[72vh] rounded-2xl object-contain shadow-2xl"
                />
              ) : (
                <span className="inline-flex items-center justify-center w-40 h-40 rounded-2xl bg-white/10 text-white">
                  {previewAttachment.kind === 'model' ? (
                    <User className="w-16 h-16" />
                  ) : (
                    <ImageIcon className="w-16 h-16" />
                  )}
                </span>
              );
            })()}
            {previewAttachment.kind !== 'image' && (
              <span className="text-white/80 text-[15px] font-medium text-center">
                {previewAttachment.name}
              </span>
            )}
          </div>
        </div>
      )}
    </>,
    document.body,
  );
};

/** Item del menú de adjuntos del Asistente de marketing — fila full-width
 *  con icon + label + chevron. Usado tanto en el popup desktop como en el
 *  bottom sheet mobile. */
const AttachMenuItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}> = ({ icon, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl hover:bg-neutral-50 active:bg-neutral-100 text-left transition-colors"
  >
    <span className="inline-flex items-center gap-3 text-[14.5px] text-brand-black/90">
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-primary/10 text-brand-primary shrink-0">
        {icon}
      </span>
      {label}
    </span>
    <ChevronRight className="w-4 h-4 text-[#3C3C43]/40 shrink-0" />
  </button>
);
